// Computes "how complete is this profile" for the account-page checklist.
// Recalculated live from the `user` object already in AuthContext — no
// separate endpoint needed. Runs against `user.role` (the ACTIVE role), so
// a user who just switched roles automatically sees the checklist for their
// new role's requirements instead of the one they already completed.

const hasRate = (categoryDetails = {}) =>
  Object.values(categoryDetails || {}).some((d) => d?.rate);

export function getProfileChecklist(user) {
  if (!user || user.role === "student" || user.role === "pending") return null;

  const isInstitution = user.role === "institution";

  const items = [
    isInstitution
      ? {
          key: "academyName",
          label: "Add your training center name",
          nav: { tab: "profile", subTab: "about" },
          done: !!user.academyName?.trim(),
        }
      : {
          key: "education",
          label: "Add your education details",
          nav: { tab: "profile", subTab: "career" },
          done: !!(user.degree?.trim() && user.schoolName?.trim()),
        },
    {
      key: "picture",
      label: "Add a profile photo",
      nav: { tab: "profile", subTab: "about" },
      done: !!user.picture?.url,
    },
    {
      key: "coverImage",
      label: "Add a cover image",
      // Edited from the public profile preview page, not an account tab —
      // `href` (a full route) is a distinct shape from the `{tab, subTab}`
      // in-page nav the other items use; ProfileChecklist renders whichever
      // is present as a real link vs an in-page tab switch.
      nav: {
        href: `/${isInstitution ? "academy" : "tutor"}/${user.slug || user._id}`,
      },
      done: !!user.coverImage?.url,
    },
    {
      key: "bio",
      label: "Write your bio",
      nav: { tab: "profile", subTab: "about" },
      done: !!(user.bioIntro || user.about || "").trim(),
    },
    {
      key: "location",
      label: "Add your location",
      nav: { tab: "profile", subTab: "about" },
      done: !!user.location?.address,
    },
    {
      key: "kyc",
      label: "Complete KYC verification",
      nav: { tab: "profile", subTab: "about" },
      done: ["found", "verified"].includes(user.ninVerification?.status),
    },
    {
      key: "teachingModes",
      label: "Select your teaching modes",
      nav: { tab: "programs" },
      done: (user.teachingModes || []).length > 0,
    },
    {
      key: "pricing",
      label: "Set your pricing",
      nav: { tab: "programs" },
      done: hasRate(user.categoryDetails),
    },
    {
      key: "media",
      label: "Upload an introduction video",
      nav: { tab: "profile", subTab: "media" },
      done: (user.videos || []).length > 0,
    },
  ];

  const doneCount = items.filter((i) => i.done).length;
  const percent = Math.round((doneCount / items.length) * 100);

  return { percent, items };
}
